import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../services/api';
import toast from 'react-hot-toast';
import { FaUser, FaCheckCircle } from 'react-icons/fa';

const CompleteProfile = () => {
  const navigate = useNavigate();
  const { currentUser, fetchUserProfile } = useAuth();
  const [loading, setLoading] = useState(false);
  const [checkingUsername, setCheckingUsername] = useState(false);
  const [usernameAvailable, setUsernameAvailable] = useState(null);

  const [formData, setFormData] = useState({
    username: '',
    displayName: currentUser?.displayName || '',
    bio: '',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    // Check username availability as user types
    if (name === 'username') {
      checkUsernameAvailability(value);
    }
  };

  const checkUsernameAvailability = async (username) => {
    // Reset if empty
    if (!username || username.length < 3) {
      setUsernameAvailable(null);
      return;
    }

    // Validate format (alphanumeric only)
    if (!/^[a-zA-Z0-9]+$/.test(username)) {
      setUsernameAvailable(false);
      return;
    }

    setCheckingUsername(true);
    try {
      await authAPI.getUserByUsername(username);
      // If we get here, username exists
      setUsernameAvailable(false);
    } catch (error) {
      if (error.response?.status === 404) {
        // Username doesn't exist, it's available!
        setUsernameAvailable(true);
      } else {
        setUsernameAvailable(null);
      }
    } finally {
      setCheckingUsername(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate username
    if (!formData.username || formData.username.length < 3) {
      toast.error('Username must be at least 3 characters');
      return;
    }

    if (!/^[a-zA-Z0-9]+$/.test(formData.username)) {
      toast.error('Username can only contain letters and numbers');
      return;
    }

    if (!usernameAvailable) {
      toast.error('Please choose an available username');
      return;
    }

    setLoading(true);
    try {
      await authAPI.register({
        username: formData.username,
        displayName: formData.displayName,
        bio: formData.bio,
      });

      // Fetch the new profile
      await fetchUserProfile();

      toast.success('Profile completed successfully!');
      navigate('/');
    } catch (error) {
      console.error('Complete profile error:', error);
      const errorMessage = error.response?.data?.error || 'Failed to complete profile';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <div className="w-20 h-20 bg-gradient-to-br from-boop-primary to-boop-secondary rounded-full flex items-center justify-center">
              <FaUser className="text-3xl text-white" />
            </div>
          </div>
          <h2 className="text-3xl font-bold mb-2">Complete Your Profile</h2>
          <p className="text-gray-600">
            Choose your unique username to get started on Boop!
          </p>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-md">
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Username */}
            <div>
              <label className="block text-sm font-semibold mb-2" htmlFor="username">
                Username *
              </label>
              <div className="relative">
                <input
                  type="text"
                  id="username"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  placeholder="Choose a unique username"
                  className={`w-full px-4 py-3 border rounded-lg pr-10 ${
                    formData.username.length >= 3
                      ? usernameAvailable
                        ? 'border-green-500 focus:ring-green-500'
                        : 'border-red-500 focus:ring-red-500'
                      : 'border-gray-300 focus:ring-boop-primary'
                  } focus:outline-none focus:ring-2`}
                  required
                  minLength={3}
                  maxLength={50}
                  pattern="[a-zA-Z0-9]+"
                  title="Only letters and numbers allowed"
                />
                {checkingUsername && (
                  <div className="absolute right-3 top-3">
                    <div className="animate-spin h-5 w-5 border-2 border-boop-primary border-t-transparent rounded-full"></div>
                  </div>
                )}
                {!checkingUsername && formData.username.length >= 3 && usernameAvailable !== null && (
                  <div className="absolute right-3 top-3">
                    {usernameAvailable ? (
                      <FaCheckCircle className="text-green-500 text-xl" />
                    ) : (
                      <span className="text-red-500 text-xl">✗</span>
                    )}
                  </div>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                3-50 characters, letters and numbers only
              </p>
              {formData.username.length >= 3 && usernameAvailable === false && (
                <p className="text-xs text-red-500 mt-1">
                  This username is already taken. Try another one!
                </p>
              )}
              {formData.username.length >= 3 && usernameAvailable === true && (
                <p className="text-xs text-green-500 mt-1">
                  Great! This username is available
                </p>
              )}
            </div>

            {/* Display Name */}
            <div>
              <label className="block text-sm font-semibold mb-2" htmlFor="displayName">
                Display Name
              </label>
              <input
                type="text"
                id="displayName"
                name="displayName"
                value={formData.displayName}
                onChange={handleChange}
                placeholder="Your display name"
                className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-boop-primary"
                maxLength={100}
              />
              <p className="text-xs text-gray-500 mt-1">
                This is how your name will appear to others
              </p>
            </div>

            {/* Bio */}
            <div>
              <label className="block text-sm font-semibold mb-2" htmlFor="bio">
                Bio (Optional)
              </label>
              <textarea
                id="bio"
                name="bio"
                value={formData.bio}
                onChange={handleChange}
                placeholder="Tell us a bit about yourself..."
                className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-boop-primary"
                rows={3}
                maxLength={500}
              />
              <p className="text-xs text-gray-500 mt-1">
                {formData.bio.length}/500 characters
              </p>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading || !usernameAvailable || checkingUsername}
              className="w-full bg-boop-primary text-white py-3 rounded-lg hover:bg-boop-secondary transition disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
            >
              {loading ? 'Completing Profile...' : 'Complete Profile'}
            </button>
          </form>
        </div>

        <div className="mt-6 bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
          <p className="text-sm text-blue-800">
            <strong>Note:</strong> Your username cannot be changed later, so choose carefully!
          </p>
        </div>
      </div>
    </div>
  );
};

export default CompleteProfile;
